import { LogisticsProjectLookupComponent } from '../../../../logistics-projects/logistics-project-lookup.component';
import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
﻿import { Products } from '../../../../order/models/product.model';
import { AccountingCatalogDescriptionComponent, AccountingCatalog } from '../../../shared/accounting-catalog-description.component';
import { AccountingEditorPartyState } from '../../../shared/accounting-editor-party-state';
import { AccountingDraftEditRequest } from '../../../shared/accounting-draft-edit.model';
import { RatedEditorLine, rateSnapshot, recalculateRatedLine, validRatedLine, restoreTaxCodes } from '../../../shared/accounting-editor-line';
import { currencyFractionDigits, roundCurrency } from '../../../../shared/utils/commercial-money';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import * as XLSX from 'xlsx';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../../../shared/page-layout/page-form-section.component';
import { AccountsService } from '../../../accounts.service';
import { AccountingTaxCode } from '../../../accounting-tax-code.model';
import { recalculateLineTax, taxRateForCode } from '../../../shared/accounting-line-tax';
import { accountingNumberWidth } from '../../../shared/accounting-line-width';
import { ManualEntryReasonCode } from '../../../manual-entry-control.model';
import { AccountingAttachmentsPanelComponent } from '../../../shared/accounting-attachments-panel.component';
import { Invoice } from '../../../invoice/invoice.model';
import { PaymentTerm } from '../../../../order/models/payment-term.model';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { LogisticsProjectsService } from '../../../../logistics-projects/logistics-projects.service';
import { CrmClient, CrmClientCommunication } from '../../../../crm/crm.model';
import { Party } from '../../../../party/party.model';
import { PartyService } from '../../../../party/party.service';
import { PartyType } from '../../../../party/party-type.enum';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../../../../shared/crm-client-contact-picker/crm-party-context.service';
import { CommercialPartyPickerComponent } from '../../../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../../../shared/commercial-party-picker/commercial-party.model';
import { CrmQuickCreateResult } from '../../../../shared/crm-quick-create/crm-quick-create-dialog.component';

interface ManualInvoiceLine extends RatedEditorLine {
    savedTaxCode?: string | null;
    invoiceLineId?: number;
    itemDescription: string;
    itemDescriptionLocal: string;
    invoiceComment: string;
    reference: string;
    quantity: number;
    amountExclTax: number;
    taxAmount: number;
    taxCode: string;
}

@Component({
    selector: 'app-manual-invoice-draft',
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,CommonModule, FormsModule, ButtonModule, DatePickerModule, FileUploadModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TextareaModule, ToastModule, PageShellComponent, PageHeroComponent, PageFormSectionComponent, AccountingAttachmentsPanelComponent, CommercialPartyPickerComponent, AccountingCatalogDescriptionComponent],
    providers: [MessageService, AccountingEditorPartyState],
    templateUrl: './manual-invoice-draft.component.html',
    styleUrls: ['./manual-invoice-draft.component.css', '../../../shared/accounting-line-layout.css']
})
export class ManualInvoiceDraftComponent implements OnInit {
    readonly numberInputWidth = accountingNumberWidth;
    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };
    crmClients: CrmClient[] = [];
    parties: Party[] = [];
    crmContacts: CrmClientCommunication[] = [];
    reasonCodeOptions: ManualEntryReasonCode[] = [];
    taxCodeOptions: AccountingTaxCode[] = [];
    paymentTermOptions: PaymentTerm[] = [];

    selectedCrmClient?: CrmClient | null;
    selectedCrmContact?: CrmClientCommunication | null;
    selectedParty?: Party | null;
    logisticsProjectId?: number;
    invoiceDate = new Date();
    dueDate = new Date();
    paymentTerms = '';
    sourceReference = '';
    reasonCode = 'NON_SOURCE_INVOICE';
    reasonText = '';
    defaultTaxCode = 'VAT';
    validationMessage = '';
    documentStatus = 'DRAFT';
    lines: ManualInvoiceLine[] = [this.blankLine()];
    invoiceId?: number;
    private rowVersion?: number;
    saving = false;
    loading = false;
    loadFailed = false;
    editable = true;
    sourceType = 'MANUAL';
    catalog: Products[] = [];
    private loadedInvoice?: Invoice;

    constructor(private accountsService: AccountsService, private sessionStorage: SessionStorageService, private messageService: MessageService, private route: ActivatedRoute, private router: Router, private logisticsProjectsService: LogisticsProjectsService, private crmPartyContext: CrmPartyContextService, private partyService: PartyService, public partyState: AccountingEditorPartyState, private catalogs: AccountingCatalog) {}

    ngOnInit(): void {
        const companyId = this.sessionStorage.getCompany()?.id;
        if (companyId) {
            this.catalogs.load(companyId).subscribe({ next: rows => this.catalog = rows.filter(p => !p.deletedAt && p.owningCompanyId === companyId), error: () => this.catalog = [] });
            this.crmPartyContext.loadClients(companyId, CRM_RECEIVABLE_ROLES).subscribe(clients => {
                this.crmClients = clients;

            });
            this.partyService.getParties(companyId, Object.values(PartyType), '', 0, 1000).subscribe(page => this.parties = page.content || []);
            this.loadMasterData(companyId);
        }
        this.logisticsProjectId = Number(this.route.snapshot.queryParamMap.get('projectId') || '') || undefined;
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (id) { this.loading = true; this.loadInvoice(id); }
    }

    get isEditMode(): boolean { return !!this.invoiceId; }
    get totalExclTax(): number { return this.lines.reduce((sum, line) => sum + Number(line.amountExclTax || 0), 0); }
    get totalTax(): number { return this.lines.reduce((sum, line) => sum + Number(line.taxAmount || 0), 0); }
    get totalAmount(): number { return this.totalExclTax + this.totalTax; }
    get canSubmit(): boolean {
        return (!this.invoiceId || this.rowVersion !== undefined) && !this.loading && !this.loadFailed && this.editable && !this.partyState.busy && !this.partyState.error
            && !!this.partyState.party?.id && !!this.sourceReference && !!this.reasonCode && !!this.reasonText.trim()
            && this.lines.length > 0
            && this.lines.every(line => line.itemDescription.trim() && line.taxCode && validRatedLine(line))
            && (!this.requiresBilingualDescriptions
                || this.lines.every(line => this.isArabicOnly(line.itemDescriptionLocal)));
    }
    get requiresBilingualDescriptions(): boolean {
        return !!this.sessionStorage.getCompany()?.requireBilingualSingleInvoice;
    }

    addLine(): void { if (this.sourceType !== 'MANUAL') return; this.lines = [...this.lines, this.blankLine()]; }
    removeLine(index: number): void { if (this.sourceType !== 'MANUAL') return; this.lines = this.lines.length > 1 ? this.lines.filter((_, i) => i !== index) : this.lines; }
    lineTotal(line: ManualInvoiceLine): number { return Number(line.amountExclTax || 0) + Number(line.taxAmount || 0); }
    money(value?: number): string { const digits = currencyFractionDigits(this.currencyCode); return `${this.currencyCode} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`; }
    lineTabIndex(rowIndex: number, columnIndex: number): number { return 100 + rowIndex * 20 + columnIndex; }

    applyDefaultTaxCode(): void {
        this.lines = this.lines.map(line => line.taxCode
            ? line
            : recalculateLineTax({ ...line, taxCode: this.defaultTaxCode }, this.taxCodeOptions, this.currencyCode));
    }
    recalculateLineTax(line: ManualInvoiceLine): void {
        line.recalculateTax = true;
        Object.assign(line, recalculateLineTax(line, this.taxCodeOptions, this.currencyCode));
    }

    onProductSelected(line: ManualInvoiceLine, product: Products): void {
        line.productId = product.productID; line.productCode = product.productCode; line.incomeAccountId = product.incomeAccountId;
        line.itemDescription = product.description || product.name; line.itemDescriptionLocal = product.descriptionLocal || '';
        line.unitRate = Number(product.sellingRate ?? product.standardRate ?? 0); this.recalculateLine(line);
    }
    clearCatalog(line: ManualInvoiceLine): void { line.productId = undefined; line.productCode = undefined; line.incomeAccountId = undefined; this.recalculateLine(line); }
    recalculateLine(line: ManualInvoiceLine): void { recalculateRatedLine(line, this.taxCodeOptions, this.currencyCode); }
    downloadExcelTemplate(): void {
        const rows = this.isEditMode ? this.lines.map(line => ({
            lineId: line.invoiceLineId, description: line.itemDescription, productCode: line.productCode || '',
            itemDescriptionLocal: line.itemDescriptionLocal, invoiceComment: line.invoiceComment, reference: line.reference,
            quantity: line.quantity, rate: line.unitRate, amountExclTax: line.amountExclTax, taxCode: line.taxCode
        })) : [{ description: 'Service charge', productCode: '', itemDescriptionLocal: '', invoiceComment: '', reference: 'REF-001', quantity: 1, rate: 100, taxCode: this.defaultTaxCode }];
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const buffer = XLSX.write({ Sheets: { lines: worksheet }, SheetNames: ['lines'] }, { bookType: 'xlsx', type: 'array' });
        this.downloadWorkbook(buffer, 'invoice-lines-template.xlsx');
    }
    importExcel(event: FileSelectEvent): void {
        const file = event.files?.[0]; if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const workbook = XLSX.read(reader.result, { type: 'array' });
                this.importRows(XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[workbook.SheetNames[0]]));
            } catch (error) { this.messageService.add({ severity: 'error', summary: 'Import failed', detail: error instanceof Error ? error.message : 'Invalid workbook.' }); }
        };
        reader.readAsArrayBuffer(file);
    }
    importRows(rows: Record<string, unknown>[]): void {
        const ids = rows.map(row => Number(this.pick(row, ['lineId', 'invoiceLineId'])) || undefined);
        if (this.sourceType !== 'MANUAL' && (ids.length !== this.lines.length || new Set(ids).size !== this.lines.length || ids.some(id => !this.lines.some(line => line.invoiceLineId === id))))
            throw new Error('Generated imports must retain every existing line ID and cannot add or remove lines.');
        const imported = rows.map((row, index) => {
            const saved = this.lines.find(line => line.invoiceLineId === ids[index]);
            const code = String(this.pick(row, ['productCode', 'itemNumber']) ?? '');
            const product = code ? this.catalog.find(p => p.productCode?.toLowerCase() === code.toLowerCase()) : undefined;
            if (code && !product) throw new Error(`Catalog product ${code} is not available for this company.`);
            const quantity = Number(this.pick(row, ['quantity', 'qty']) ?? 1);
            const rate = this.pick(row, ['rate', 'unitRate', 'unitPrice']) ?? (this.pick(row, ['amountExclTax', 'amount', 'netAmount', 'chargeAmount']) === undefined && product ? product.sellingRate ?? product.standardRate : undefined);
            const amount = roundCurrency(Number(this.pick(row, ['amountExclTax', 'amount', 'netAmount', 'chargeAmount']) ?? 0), this.currencyCode);
            const line: ManualInvoiceLine = { ...this.blankLine(), invoiceLineId: ids[index],
                productId: product?.productID ?? saved?.productId, productCode: product?.productCode ?? saved?.productCode, incomeAccountId: product?.incomeAccountId ?? saved?.incomeAccountId,
                itemDescription: String(this.pick(row, ['description', 'itemDescription']) ?? product?.description ?? product?.name ?? ''),
                itemDescriptionLocal: String(this.pick(row, ['itemDescriptionLocal', 'arabicDescription']) ?? product?.descriptionLocal ?? ''),
                invoiceComment: String(this.pick(row, ['invoiceComment', 'comment']) ?? ''), reference: this.sourceType !== 'MANUAL' ? saved?.reference || '' : String(this.pick(row, ['reference', 'sourceReference']) ?? ''),
                quantity, ...rateSnapshot(quantity, amount, rate === undefined ? undefined : Number(rate)), amountExclTax: amount,
                taxCode: String(this.pick(row, ['taxCode']) ?? this.defaultTaxCode), recalculateTax: true };
            line.rateEntered = true;
            if (rate !== undefined) this.recalculateLine(line); else this.recalculateLineTax(line);
            return line;
        });
        this.lines = imported.length ? imported : [this.blankLine()];
    }

    save(): void {
		if (this.saving || !this.canSubmit) return;
        this.validationMessage = '';
        const company = this.sessionStorage.getCompany();
        const country = this.sessionStorage.getCountryList()?.find(c => c.id === company?.countryId);
        const party = this.partyState.party;
        if (!company || !country || !party?.id) {
            this.validationMessage = 'Company, country, and party are required.';
            return;
        }
        const invoice = {
            invoiceId: this.invoiceId,
			rowVersion: this.rowVersion,
            mainParty: party,
            subParty: this.partyState.contact || party,
            crmClientId: this.commercialPartyReference.crmClientId ?? undefined,
            crmAccountId: this.commercialPartyReference.crmAccountId ?? undefined,
            crmClientContactId: this.commercialPartyReference.crmContactId ?? undefined,
            owningCompany: company,
            country,
            invoiceCreationDate: this.invoiceDate,
            dueDate: this.dueDate,
            paymentTerms: this.paymentTerms,
            totalAmount: this.totalAmount,
            totalAmountExclTax: this.totalExclTax,
            totalTax: this.totalTax,
            sourceType: 'MANUAL',
            sourceReference: this.sourceReference,
            manualReasonCode: this.reasonCode,
            manualReasonText: this.reasonText,
            manualAttachmentRequired: false,
            createdFromModule: 'ACCOUNTS_PORTAL',
            documentStatus: 'DRAFT',
            workflowStatus: 'DRAFT',
            postingStatus: 'NOT_POSTED',
            sentStatus: 'NOT_SENT',
            collectionStatus: 'NONE',
            invoiceLines: this.lines.map(line => ({
				invoiceLineId: line.invoiceLineId,
                itemDescription: line.itemDescription,
                itemDescriptionLocal: line.itemDescriptionLocal.trim() || undefined,
                invoiceComment: line.invoiceComment.trim() || undefined,
                itemNumber: line.productCode, productId: line.productId, unitRate: line.unitRate,
                taxCode: line.taxCode,
                quantity: line.quantity,
                chargeAmount: line.amountExclTax,
                taxAmount: line.taxAmount,
                chargeAmountInclTax: this.lineTotal(line),
                taxableAmount: line.amountExclTax,
                totalAmount: this.lineTotal(line),
                taxRate: taxRateForCode(line.taxCode, this.taxCodeOptions),
                taxCategory: this.taxCodeOptions.find(code => code.taxCode === line.taxCode)?.taxCategory,
                headerReferenceNumber: line.reference || this.sourceReference
            })),
            receipts: [],
            status: 'UNPAID',
            logisticsProjectId: this.logisticsProjectId || undefined
        } as unknown as Invoice;
		const createdNow = !this.invoiceId;
        const request = this.invoiceId ? this.accountsService.editInvoice(this.invoiceId, this.editRequest()) : this.accountsService.createManualInvoiceDraft(invoice);
		this.saving = true;
        request.subscribe({
            next: created => {
				this.hydrateInvoice(created);
                this.messageService.add({ severity: 'success', summary: 'Draft saved', detail: `Invoice ${created.invoiceNumber || created.invoiceId} saved.` });
                this.linkProjectRequirementFulfillment(created).add(() => {
					if (createdNow && created.invoiceId) this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/manual-draft', created.invoiceId]);
                });
				this.saving = false;
            },
            error: err => {
                this.validationMessage = err?.error?.message || 'Could not save manual invoice draft.';
				if (err?.status === 409 && this.invoiceId) {
					this.validationMessage = 'Draft changed elsewhere. The latest version was reloaded.';
					this.loadInvoice(this.invoiceId);
				}
				this.saving = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: this.validationMessage });
            }
        });
    }

    private linkProjectRequirementFulfillment(created: Invoice) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.sessionStorage.getCompany()?.id || 0);
        const targetResourceId = Number(created.invoiceId || this.invoiceId || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'SALES_INVOICE',
            targetResourceId,
            sourceModule: 'ACCOUNTS_RECEIVABLE',
            sourceReference: created.invoiceNumber || this.sourceReference || `Invoice #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || this.lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0) || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Invoice was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    back(): void { this.router.navigate(['/accounts/enterprise/receivables/generated-invoices']); }

    get companyId(): number | undefined { return this.sessionStorage.getCompany()?.id; }
    get actorId(): number | undefined { return this.sessionStorage.getCustomer()?.id; }

    private loadMasterData(companyId: number): void {
        this.accountsService.getManualEntryReasonCodes(companyId, 'MANUAL_CUSTOMER_INVOICE').subscribe(codes => {
            this.reasonCodeOptions = codes.length ? codes : [{ documentType: 'MANUAL_CUSTOMER_INVOICE', reasonCode: this.reasonCode, description: this.reasonCode }];
        });
        this.accountsService.getAccountingTaxCodes(companyId).subscribe(codes => {
            this.taxCodeOptions = codes.length ? codes : [{ taxCode: 'VAT', description: 'VAT', taxRate: 15 }];
            this.defaultTaxCode = this.taxCodeOptions[0]?.taxCode || this.defaultTaxCode;
            this.taxCodeOptions = restoreTaxCodes(this.lines, this.taxCodeOptions);
            this.applyDefaultTaxCode();
        });
        this.accountsService.getPaymentTerms().subscribe(terms => this.paymentTermOptions = terms);
    }

    private loadInvoice(id: number): void {
        this.loading = true;
        this.accountsService.getInvoiceById(id).subscribe({
            next: document => { this.hydrateInvoice(document); this.loading = false; this.loadFailed = false; },
            error: () => { this.loading = false; this.loadFailed = true; this.validationMessage = 'Could not load the invoice.'; }
        });
    }
    private hydrateInvoice(invoice: Invoice): void {
        this.loadedInvoice = invoice; this.invoiceId = invoice.invoiceId; this.rowVersion = invoice.rowVersion;
        this.sourceType = invoice.sourceType || 'SYSTEM'; this.documentStatus = invoice.documentStatus || 'DRAFT';
        this.editable = isEditableFinancialDraft(invoice, this.companyId);
        this.partyState.restore(invoice, this.companyId!); this.commercialPartyReference = this.partyState.reference; this.syncParty();
        this.logisticsProjectId = invoice.logisticsProjectId;
        this.invoiceDate = invoice.invoiceCreationDate ? new Date(invoice.invoiceCreationDate) : new Date();
        this.dueDate = invoice.dueDate ? new Date(invoice.dueDate) : new Date(); this.paymentTerms = invoice.paymentTerms || '';
        this.sourceReference = invoice.sourceReference || invoice.invoiceNumber || ''; this.reasonCode = invoice.manualReasonCode || this.reasonCode;
        this.reasonText = invoice.manualReasonText || '';
        this.lines = (invoice.invoiceLines || []).map(line => {
            const quantity = line.quantity ?? 1; const amount = line.chargeAmount ?? 0;
            return { invoiceLineId: line.invoiceLineId, itemDescription: line.itemDescription || '', itemDescriptionLocal: line.itemDescriptionLocal || '',
                invoiceComment: line.invoiceComment || '', reference: line.headerReferenceNumber || '', productId: line.productId,
                productCode: line.itemNumber === line.taxCode && !line.productId ? undefined : line.itemNumber,
                quantity, ...rateSnapshot(quantity, amount, line.unitRate), amountExclTax: amount, taxAmount: line.taxAmount || 0,
                savedTaxCode: line.taxCode ?? null, taxCode: line.taxCode || ((line.taxAmount || 0) > 0 ? 'VAT' : 'EXEMPT') };
        });
        if (!this.lines.length) this.lines = [this.blankLine()];
        this.taxCodeOptions = restoreTaxCodes(this.lines, this.taxCodeOptions);
    }
    onCommercialPartyChange(reference: CommercialPartyReference): void {
        this.partyState.change(reference, this.companyId!).subscribe(() => this.syncParty());
        this.commercialPartyReference = this.partyState.reference;
    }
    onCrmQuickCreated(result: CrmQuickCreateResult): void { if (result.mode === 'client') this.crmClients = [...this.crmClients, result.record]; }
    private syncParty(): void { this.selectedParty = this.partyState.party; this.selectedCrmClient = this.partyState.client; }
    private editRequest(): AccountingDraftEditRequest {
        return { rowVersion: this.rowVersion, owningCompanyId: this.companyId, mainParty: this.partyState.party,
            subParty: this.partyState.contact || this.partyState.party, crmClientId: this.commercialPartyReference.crmClientId,
            crmAccountId: this.commercialPartyReference.crmAccountId, crmClientContactId: this.commercialPartyReference.crmContactId,
            documentDate: this.invoiceDate, dueDate: this.dueDate, paymentTerms: this.paymentTerms,
            logisticsProjectId: this.logisticsProjectId, manualReasonCode: this.reasonCode, manualReasonText: this.reasonText,
            lines: this.lines.map(line => ({ lineId: line.invoiceLineId, description: line.itemDescription.trim(),
                descriptionLocal: line.itemDescriptionLocal.trim(), invoiceComment: line.invoiceComment.trim(), reference: line.reference,
                productId: line.productId, quantity: line.quantity, unitRate: line.rateEntered ? line.unitRate : null,
                taxCode: line.rateEntered || line.recalculateTax ? line.taxCode : line.savedTaxCode, recalculateTax: line.recalculateTax })) };
    }

    private blankLine(): ManualInvoiceLine {
        return { unitRate: null, rateEntered: true, recalculateTax: false, itemDescription: '', itemDescriptionLocal: '', invoiceComment: '', reference: '', quantity: 1, amountExclTax: 0, taxAmount: 0, taxCode: this.defaultTaxCode || 'VAT' };
    }

    private isArabicOnly(value?: string): boolean {
        const text = value?.trim() || '';
        return /[\u0600-\u06FF]/.test(text) && !/[A-Za-z]/.test(text);
    }

    private pick(row: Record<string, unknown>, keys: string[]): unknown {
        const normalized = Object.entries(row).reduce<Record<string, unknown>>((map, [key, value]) => {
            map[key.replace(/\s|_/g, '').toLowerCase()] = value;
            return map;
        }, {});
        return keys.map(key => normalized[key.replace(/\s|_/g, '').toLowerCase()]).find(value => value !== undefined && value !== null && value !== '');
    }

    private downloadWorkbook(buffer: ArrayBuffer, fileName: string): void {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        window.URL.revokeObjectURL(url);
    }

    private get currencyCode(): string {
        return this.sessionStorage.getCountryList()?.find(c => c.id === this.sessionStorage.getCompany()?.countryId)?.currencyCode || 'SAR';
    }
}
